import i18n from "@/Plugins/i18n.js";

const REVIEW_STATUS = {
    STATUS_APPROVED: "approved",
    STATUS_REJECTED: "rejected",
    STATUS_PENDING: "pending",
};

const { t } = i18n.global;

export const isReviewable = (object) =>
    object &&
    typeof object !== "undefined" &&
    typeof object.newValue !== "undefined";

export const getReviewStatus = (reviewableItem, allowDelay = true) => {
    if (!isReviewable(reviewableItem)) {
        return REVIEW_STATUS.STATUS_APPROVED;
    }

    if (
        allowDelay &&
        reviewableItem &&
        reviewableItem.hasOwnProperty("reviewedBy") &&
        ["system", "moderation_AI"].includes(reviewableItem.reviewedBy) &&
        reviewableItem.hasOwnProperty("status") &&
        reviewableItem.status === REVIEW_STATUS.STATUS_APPROVED &&
        reviewableItem.hasOwnProperty("approvedAt")
    ) {
        const approvedAt = new Date(reviewableItem.approvedAt);
        const now = new Date();
        const diff = (now.getTime() - approvedAt.getTime()) / 1000;
        const minutes = Math.floor(diff / 60);

        if (minutes < 3) {
            return REVIEW_STATUS.STATUS_PENDING;
        }

        return REVIEW_STATUS.STATUS_APPROVED;
    }

    if (reviewableItem && reviewableItem.hasOwnProperty("status")) {
        return reviewableItem.status;
    }

    return null;
};

export const isValueSet = (reviewableItem) => {
    if (!reviewableItem) return false;
    return (
        typeof reviewableItem !== "undefined" &&
        Boolean(reviewableItem.oldValue || reviewableItem.newValue)
    );
};

export const isReviewStatus = (status) => (reviewableItem) =>
    reviewableItem && getReviewStatus(reviewableItem) === status;

export const isStatusApproved = isReviewStatus(REVIEW_STATUS.STATUS_APPROVED);

export const isStatusRejected = isReviewStatus(REVIEW_STATUS.STATUS_REJECTED);

export const getReviewItemValue = (reviewableItem) => {
    if (!isReviewable(reviewableItem)) return reviewableItem;

    if (!isValueSet(reviewableItem)) {
        return;
    }

    if (
        reviewableItem &&
        reviewableItem.hasOwnProperty("reviewedBy") &&
        ["system", "moderation_AI"].includes(reviewableItem.reviewedBy) &&
        reviewableItem.hasOwnProperty("status") &&
        reviewableItem.status === REVIEW_STATUS.STATUS_APPROVED
    ) {
        return reviewableItem.oldValue;
    }

    return isStatusApproved(reviewableItem)
        ? reviewableItem.oldValue
        : reviewableItem.newValue;
};

export const getCharacteristicsValue = (characteristics, key) => {
    const characteristic = [...(characteristics[key] || [])].map((choice) =>
        t(`characteristics.${choice}`)
    );

    const characteristicOther = getReviewItemValue(
        characteristics[`${key}_other`]
    );

    if (characteristicOther) {
        characteristic.push(characteristicOther);
    }

    return characteristic.join(", ");
};

export const isCharacteristicSet = (characteristics, key) => {
    const trimmedKey = key.split("_")[0];

    return Boolean(
        (characteristics[key] && characteristics[key].length > 0) ||
            isValueSet(characteristics[`${trimmedKey}_other`])
    );
};

export const isCharacteristicItemVisible = (
    characteristic,
    characteristicOther
) => {
    const hasValue = isValueSet(characteristicOther);
    if (Array.isArray(characteristic)) {
        return characteristic.length;
    }
    if (hasValue) {
        return isStatusApproved(characteristicOther);
    }
    return Boolean(characteristic);
};

export const concatenateProfileItems = (items, key) => {
    if (!items || !key) return "";

    let specified = Array.isArray(
        items?.[key === "language" ? `${key}_popular` : key]
    )
        ? items[key === "language" ? `${key}_popular` : key]
        : [];

    let customValue = getReviewItemValue(items?.[`${key}_other`]);

    if (!specified.length && !customValue) return "";
    if (!customValue) return specified.join(", ");
    if (!specified.length) return customValue;

    return specified.concat(customValue).join(", ");
};

export { REVIEW_STATUS };
